<template>
  <div class="dash-canvas p-6 space-y-6 min-h-full">
    <!-- Filters -->
    <div class="flex flex-wrap items-center justify-end gap-2">
        <!-- Date range filter -->
        <div class="flex items-center p-1 bg-white border border-gray-200 rounded-xl shadow-sm">
          <span class="pl-3 pr-1 text-gray-400 material-icons-round">event</span>
          <v-date-picker v-model="range" is-range timezone="UTC">
            <template v-slot="{ inputValue, inputEvents }">
              <div class="flex items-center">
                <input
                  :value="inputValue.start"
                  v-on="inputEvents.start"
                  placeholder="From"
                  class="w-24 h-8 px-2 text-sm text-gray-700 bg-transparent rounded-lg focus:outline-none focus:bg-taxiye-50"
                />
                <span class="mx-1 text-base text-gray-300 material-icons-round">arrow_forward</span>
                <input
                  :value="inputValue.end"
                  v-on="inputEvents.end"
                  placeholder="To"
                  class="w-24 h-8 px-2 text-sm text-gray-700 bg-transparent rounded-lg focus:outline-none focus:bg-taxiye-50"
                />
              </div>
            </template>
          </v-date-picker>
          <button
            v-if="range.start || range.end"
            @click="range = { start: null, end: null }"
            type="button"
            title="Clear filter"
            class="flex items-center justify-center w-8 h-8 mr-1 text-gray-400 rounded-lg transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 focus:outline-none"
          >
            <span class="text-lg material-icons-round">close</span>
          </button>
        </div>

        <!-- Refresh -->
        <button
          @click="refresh"
          type="button"
          title="Refresh data"
          class="flex items-center justify-center w-10 h-10 text-gray-400 bg-white border border-gray-200 rounded-xl shadow-sm transition-colors duration-150 hover:text-taxiye-600 hover:border-taxiye-200 focus:outline-none"
        >
          <span class="text-xl material-icons-round" :class="{ spin: loading }">refresh</span>
        </button>
    </div>

    <!-- Trip pulse + fleet stats -->
    <div class="grid gap-4 lg:grid-cols-3">
      <!-- Trip pulse -->
      <section class="pulse-card relative flex flex-col p-6 overflow-hidden border border-gray-100 rounded-2xl">
        <i class="watermark text-taxiye-500 material-icons-round">local_taxi</i>

        <div class="relative flex items-start justify-between">
          <div>
            <p class="text-xs font-extrabold tracking-widest text-gray-600 uppercase">Total Trips</p>
            <p class="mt-2 text-4xl font-black leading-none text-gray-900">{{ formatNumber(stats.totalTrips) }}</p>
          </div>
          <router-link
            to="/rides"
            class="inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full shadow-sm bg-taxiye-100 text-taxiye-600 transition-colors duration-150 hover:bg-taxiye-50"
          >
            All trips
            <i class="text-sm material-icons-round">chevron_right</i>
          </router-link>
        </div>

        <!-- Distribution bar -->
        <div class="relative flex h-3 mt-6 overflow-hidden bg-gray-200 rounded-full shadow-inner">
          <div
            v-for="segment in tripSegments"
            :key="segment.key"
            :title="segment.label + ': ' + segment.pct + '%'"
            class="h-full transition-all duration-500"
            :class="segment.bar"
            :style="{ width: segment.pct + '%' }"
          ></div>
        </div>

        <!-- Legend -->
        <ul class="relative mt-5 space-y-1">
          <li v-for="segment in tripSegments" :key="segment.key">
            <router-link
              :to="segment.to"
              class="legend-row group flex items-center justify-between px-3 py-2 -mx-3 rounded-xl transition-colors duration-150"
            >
              <span class="flex items-center text-sm font-bold text-gray-800 group-hover:text-gray-900">
                <span class="w-2 h-2 mr-3 rounded-full" :class="segment.bar"></span>
                {{ segment.label }}
              </span>
              <span class="flex items-center">
                <span class="text-sm font-extrabold text-gray-900">{{ formatNumber(segment.value) }}</span>
                <span class="w-10 ml-3 px-2 py-1 text-xs font-bold leading-none text-center text-gray-600 bg-gray-100 rounded-full">{{ segment.pct }}%</span>
              </span>
            </router-link>
          </li>
        </ul>

        <!-- Footer stats -->
        <div class="relative grid grid-cols-2 gap-4 pt-5 mt-auto border-t border-gray-200">
          <div class="flex items-center">
            <span class="flex items-center justify-center flex-shrink-0 w-10 h-10 mr-3 text-green-600 bg-green-100 rounded-xl">
              <i class="text-lg material-icons-round">check_circle</i>
            </span>
            <div>
              <p class="text-xs font-extrabold tracking-wide text-gray-600 uppercase">Completion</p>
              <p class="mt-1 text-lg font-black leading-none text-gray-900">{{ completionRate }}%</p>
            </div>
          </div>
          <div class="flex items-center">
            <span class="flex items-center justify-center flex-shrink-0 w-10 h-10 mr-3 rounded-xl bg-taxiye-100 text-taxiye-600">
              <i class="text-lg material-icons-round">payments</i>
            </span>
            <div class="min-w-0">
              <p class="text-xs font-extrabold tracking-wide text-gray-600 uppercase">Revenue</p>
              <p class="mt-1 text-lg font-black leading-none text-gray-900 truncate">
                {{ formatNumber(stats.revenue) }}
                <span class="text-xs font-medium text-gray-400">ETB</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      <!-- Fleet & people -->
      <div class="grid gap-4 sm:grid-cols-2 lg:col-span-2">
        <router-link
          v-for="stat in fleetMetrics"
          :key="stat.label"
          :to="stat.to"
          class="stat-card group relative flex flex-col justify-between p-5 overflow-hidden bg-white border border-gray-100 rounded-2xl"
        >
          <i class="watermark material-icons-round" :class="stat.iconText">{{ stat.icon }}</i>
          <div class="relative flex items-start justify-between">
            <div class="flex items-center justify-center flex-shrink-0 w-12 h-12 rounded-xl shadow-sm" :class="stat.iconBg">
              <i class="text-2xl material-icons-round" :class="stat.iconText">{{ stat.icon }}</i>
            </div>
            <i class="text-base text-gray-300 material-icons-round transition-colors duration-150 group-hover:text-taxiye-500">north_east</i>
          </div>
          <div class="relative mt-4 min-w-0">
            <p class="text-3xl font-black leading-none text-gray-900">{{ formatNumber(stat.value) }}</p>
            <p class="mt-2 text-xs font-extrabold tracking-widest text-gray-600 uppercase truncate">{{ stat.label }}</p>
          </div>
        </router-link>
      </div>
    </div>

    <!-- Recent trips -->
    <section class="overflow-hidden bg-white border border-gray-200 rounded-xl shadow-sm">
      <div class="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div class="flex items-center space-x-2">
          <h2 class="text-base font-bold text-gray-900">Recent Trips</h2>
          <span
            v-if="latestRides.length"
            class="inline-flex items-center justify-center px-2 py-1 text-xs font-semibold leading-none rounded-full bg-taxiye-100 text-taxiye-600"
          >{{ latestRides.length }}</span>
        </div>
        <router-link
          to="/rides"
          class="inline-flex items-center text-sm font-medium text-taxiye-600 transition-colors duration-150 hover:text-taxiye-500"
        >
          View all
          <i class="text-base material-icons-round">chevron_right</i>
        </router-link>
      </div>

      <div class="overflow-x-auto">
        <table class="min-w-full text-sm">
          <thead>
            <tr class="bg-gray-100 border-b border-gray-200">
              <th
                v-for="col in columns"
                :key="col"
                class="px-5 py-3 text-xs font-semibold tracking-wider text-left text-gray-500 uppercase whitespace-no-wrap"
              >{{ col }}</th>
              <th class="px-5 py-3"></th>
            </tr>
          </thead>

          <!-- Loading skeleton -->
          <tbody v-if="loading && !latestRides.length">
            <tr v-for="n in 5" :key="'skeleton-' + n" class="border-b border-gray-100">
              <td class="px-5 py-4">
                <div class="flex items-center space-x-3">
                  <span class="skeleton w-10 h-10 rounded-full"></span>
                  <span class="skeleton w-24 h-3 rounded-full"></span>
                </div>
              </td>
              <td class="px-5 py-4">
                <div class="flex items-center space-x-3">
                  <span class="skeleton w-10 h-10 rounded-full"></span>
                  <span class="skeleton w-24 h-3 rounded-full"></span>
                </div>
              </td>
              <td class="px-5 py-4"><span class="skeleton block w-20 h-3 rounded-full"></span></td>
              <td class="px-5 py-4">
                <span class="skeleton block w-32 h-3 mb-2 rounded-full"></span>
                <span class="skeleton block w-24 h-3 rounded-full"></span>
              </td>
              <td class="px-5 py-4"><span class="skeleton block w-16 h-5 rounded-lg"></span></td>
              <td class="px-5 py-4"><span class="skeleton block w-24 h-3 rounded-full"></span></td>
              <td class="px-5 py-4"><span class="skeleton block w-12 h-3 rounded-full"></span></td>
              <td class="px-5 py-4"><span class="skeleton block w-20 h-5 rounded-full"></span></td>
              <td class="px-5 py-4"></td>
            </tr>
          </tbody>

          <tbody v-else-if="latestRides.length">
            <tr
              v-for="(trip, index) in latestRides"
              :key="trip._id"
              class="transition-colors duration-150 hover:bg-taxiye-50"
              :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-100'"
            >
              <!-- Passenger -->
              <td class="px-5 py-4 whitespace-no-wrap">
                <div class="flex items-center space-x-3">
                  <span
                    class="flex items-center justify-center flex-shrink-0 w-10 h-10 text-xs font-bold rounded-full"
                    :class="avatarClass(personLabel(trip.passenger))"
                  >{{ initials(personLabel(trip.passenger)) }}</span>
                  <span class="font-medium text-gray-900">{{ personLabel(trip.passenger, "Unknown") }}</span>
                </div>
              </td>
              <!-- Driver -->
              <td class="px-5 py-4 whitespace-no-wrap">
                <div class="flex items-center space-x-3">
                  <span
                    class="flex items-center justify-center flex-shrink-0 w-10 h-10 text-xs font-bold rounded-full"
                    :class="avatarClass(personLabel(trip.driver))"
                  >{{ initials(personLabel(trip.driver)) }}</span>
                  <span class="text-gray-700">{{ personLabel(trip.driver, "Unknown") }}</span>
                </div>
              </td>
              <!-- Dispatcher -->
              <td class="px-5 py-4 text-gray-500 whitespace-no-wrap">
                {{ personLabel(trip.dispatcher, "—") }}
              </td>
              <!-- Route -->
              <td class="px-5 py-4">
                <div class="flex">
                  <div class="flex flex-col items-center mr-3">
                    <i class="text-base leading-none text-green-500 material-icons-round">my_location</i>
                    <span class="flex-1 w-px my-1 border-l border-gray-300 border-dashed"></span>
                    <i class="text-base leading-none text-red-500 material-icons-round">flag</i>
                  </div>
                  <div class="flex flex-col justify-between min-w-0">
                    <p class="text-gray-700 truncate max-w-xs">{{ trip.pickUpAddress ? trip.pickUpAddress.name : "—" }}</p>
                    <p class="text-gray-700 truncate max-w-xs">{{ trip.dropOffAddress ? trip.dropOffAddress.name : "—" }}</p>
                  </div>
                </div>
              </td>
              <!-- Type -->
              <td class="px-5 py-4 whitespace-no-wrap">
                <span class="inline-flex px-2 py-1 text-xs font-medium text-gray-600 capitalize rounded-md bg-gray-100">{{ trip.type }}</span>
              </td>
              <!-- Date -->
              <td class="px-5 py-4 text-gray-600 whitespace-no-wrap">
                <div class="flex items-center space-x-2">
                  <i class="text-sm text-gray-400 material-icons-round">schedule</i>
                  <span>{{ formatDate(trip.pickupTimestamp) }}</span>
                </div>
                <p class="mt-1 ml-6 text-xs text-gray-400">{{ formatDate(trip.endTimestamp) }}</p>
              </td>
              <!-- Fare -->
              <td class="px-5 py-4 whitespace-no-wrap">
                <span class="font-semibold text-gray-900">{{ trip.fare ? trip.fare.toFixed(2) : "0.00" }}</span>
                <span class="ml-1 text-xs text-gray-400">ETB</span>
              </td>
              <!-- Status -->
              <td class="px-5 py-4 whitespace-no-wrap">
                <span
                  class="inline-flex items-center px-2 py-1 text-xs font-semibold leading-none capitalize rounded-full"
                  :class="statusClass(trip.status)"
                >
                  <span class="w-2 h-2 mr-2 rounded-full" :class="statusDotClass(trip.status)"></span>
                  {{ trip.status }}
                </span>
              </td>
              <!-- Action -->
              <td class="px-5 py-4 text-right whitespace-no-wrap">
                <a
                  :href="'/rides/' + trip._id + '/show'"
                  target="self"
                  class="inline-flex items-center justify-center w-10 h-10 text-gray-400 rounded-lg transition-colors duration-150 hover:bg-taxiye-100 hover:text-taxiye-600"
                >
                  <i class="material-icons-round">visibility</i>
                </a>
              </td>
            </tr>
          </tbody>

          <!-- Empty state -->
          <tbody v-else>
            <tr>
              <td colspan="9" class="py-16 text-center">
                <div class="flex flex-col items-center text-gray-400">
                  <span class="flex items-center justify-center w-16 h-16 mb-3 rounded-full bg-gray-100">
                    <i class="text-3xl material-icons-round">inbox</i>
                  </span>
                  <span class="text-sm font-medium text-gray-500">No recent trips</span>
                  <span class="mt-1 text-xs text-gray-400">Trips will appear here once available</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: "Dashboard",
  data() {
    return {
      stats: {
        totalTrips: 0,
        totalUsers: 0,
        totalDrivers: 0,
        totalCompletedTrips: 0,
        totalRunningTrips: 0,
        totalCanceledTrips: 0,
        totalActiveFleets: 0,
        revenue: 0,
      },
      latestRides: [],
      loading: false,
      range: {
        start: null,
        end: null,
      },
      columns: [
        "Passenger",
        "Driver",
        "Dispatcher",
        "Route",
        "Type",
        "Date",
        "Fare",
        "Status",
      ],
    };
  },
  created() {
    this.getStats();
    this.getLatestRides();
  },
  computed: {
    dataRefreshTrigger() {
      return [this.range.start, this.range.end].join("|");
    },
    fleetMetrics() {
      return [
        { label: "Total Drivers", value: this.stats.totalDrivers, icon: "person", iconBg: "bg-indigo-50", iconText: "text-indigo-600", to: "/drivers" },
        { label: "Total Passengers", value: this.stats.totalUsers, icon: "directions_walk", iconBg: "bg-blue-50", iconText: "text-blue-600", to: "/passengers" },
        { label: "Active Fleets", value: this.stats.activeVehicles, icon: "local_taxi", iconBg: "bg-green-50", iconText: "text-green-600", to: "/vehicles?status=active" },
        { label: "Online Fleets", value: this.stats.totalActiveFleets, icon: "wifi", iconBg: "bg-teal-50", iconText: "text-teal-600", to: "/vehicles?status=online" },
      ];
    },
    tripSegments() {
      const total = this.stats.totalTrips || 0;
      return [
        { key: "completed", label: "Completed", value: this.stats.totalCompletedTrips || 0, bar: "bg-green-500", to: "/rides?status=Completed" },
        { key: "running", label: "Running", value: this.stats.totalRunningTrips || 0, bar: "bg-orange-400", to: "/rides?status=Started" },
        { key: "cancelled", label: "Cancelled", value: this.stats.totalCanceledTrips || 0, bar: "bg-red-500", to: "/rides?status=CANCELLED" },
      ].map((segment) => ({
        ...segment,
        pct: total ? Math.round((segment.value / total) * 100) : 0,
      }));
    },
    completionRate() {
      const total = this.stats.totalTrips || 0;
      return total ? Math.round(((this.stats.totalCompletedTrips || 0) / total) * 100) : 0;
    },
  },
  watch: {
    dataRefreshTrigger() {
      this.refresh();
    },
  },
  methods: {
    refresh() {
      this.getStats();
      this.getLatestRides();
    },
    getStats() {
      this.$misc
        .get("dashboard", {
          params: {
            from: this.range.start ? this.range.start : undefined,
            to: this.range.end ? this.range.end : undefined,
          },
        })
        .then((response) => (this.stats = response.data))
        .catch((err) => console.log(err));
    },
    getLatestRides() {
      this.loading = true;
      this.$misc
        .get('trips?populate=["driver","passenger","dispatcher"]', {
          params: {
            from: this.range.start ? this.range.start : undefined,
            to: this.range.end ? this.range.end : undefined,
          },
        })
        .then((response) => (this.latestRides = response.data.data))
        .catch((err) => console.log(err))
        .finally(() => (this.loading = false));
    },
    formatNumber(value) {
      return (value || 0).toLocaleString();
    },
    formatDate(date) {
      return date ? date.replace("T", " ").slice(0, 16) : "";
    },
    statusClass(status) {
      const s = (status || "").toLowerCase();
      if (s.includes("cancel")) return "text-red-700 bg-red-100";
      if (s.includes("complet")) return "text-green-700 bg-green-100";
      if (s.includes("start") || s.includes("running")) return "text-orange-700 bg-orange-100";
      return "text-gray-700 bg-gray-100";
    },
    statusDotClass(status) {
      const s = (status || "").toLowerCase();
      if (s.includes("cancel")) return "bg-red-500";
      if (s.includes("complet")) return "bg-green-500";
      if (s.includes("start") || s.includes("running")) return "bg-orange-500";
      return "bg-gray-400";
    },
    personLabel(person, fallback = "") {
      if (!person) return fallback;
      const name = `${person.firstName || ""} ${person.lastName || ""}`.trim();
      if (!name) return fallback;
      return name
        .split(" ")
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
    },
    initials(name) {
      if (!name) return "?";
      return name
        .split(" ")
        .filter(Boolean)
        .map((part) => part[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();
    },
    avatarClass(name) {
      const palette = [
        "bg-taxiye-100 text-taxiye-600",
        "bg-indigo-50 text-indigo-600",
        "bg-blue-50 text-blue-600",
        "bg-green-50 text-green-600",
        "bg-purple-50 text-purple-600",
        "bg-pink-50 text-pink-600",
      ];
      let hash = 0;
      for (let i = 0; i < (name || "").length; i++) {
        hash = (hash + name.charCodeAt(i)) % palette.length;
      }
      return palette[hash];
    },
  },
};
</script>

<style scoped>
/* Page backdrop — warm gray so the white cards stand out */
.dash-canvas {
  background: linear-gradient(180deg, #f4f3ef 0%, #eeece6 100%);
}

/* Trip pulse hero — warm gradient matching the sidebar */
.pulse-card {
  background: linear-gradient(135deg, #ffffff 0%, #fffbf2 100%);
  box-shadow: 0 2px 4px rgba(77, 50, 0, 0.04), 0 12px 24px -8px rgba(77, 50, 0, 0.12);
}

/* Oversized faded icon in the card corner */
.watermark {
  position: absolute;
  right: -1rem;
  bottom: -1.25rem;
  font-size: 6.5rem;
  line-height: 1;
  opacity: 0.07;
  transform: rotate(-12deg);
  pointer-events: none;
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.stat-card:hover .watermark {
  opacity: 0.14;
  transform: rotate(-6deg) scale(1.05);
}

/* Stat cards — elevated at rest, lift higher on hover */
.stat-card {
  box-shadow: 0 2px 4px rgba(77, 50, 0, 0.04), 0 12px 24px -8px rgba(77, 50, 0, 0.12);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}
.stat-card:hover {
  transform: translateY(-3px);
  border-color: #e2e8f0;
  box-shadow: 0 4px 8px rgba(77, 50, 0, 0.05), 0 20px 36px -10px rgba(77, 50, 0, 0.18);
}

/* Legend rows in the trip pulse card */
.legend-row:hover {
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 1px 3px rgba(77, 50, 0, 0.08);
}

/* Loading skeleton shimmer */
.skeleton {
  background: linear-gradient(90deg, #edf2f7 25%, #f7fafc 50%, #edf2f7 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s ease infinite;
}
@keyframes shimmer {
  from {
    background-position: 200% 0;
  }
  to {
    background-position: -200% 0;
  }
}

.spin {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
